import React, { useEffect, useRef, useState } from 'react';
import { Z_INDEX } from '@/constants/z-index';
import { lightTheme } from '@/customTheme';
import AiGlowCircle from '@/components/AiGlowCircle/AiGlowCircle';

// ─── Seeded 32-bit LCG ───────────────────────────────────────────────────────
function makeLcg(seed: number) {
  let s = seed >>> 0;
  return {
    next(): number {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
      return s / 0xffffffff;
    },
    randRange(lo: number, hi: number): number {
      return lo + this.next() * (hi - lo);
    },
    randInt(lo: number, hi: number): number {
      return lo + Math.floor(this.next() * (hi - lo + 1));
    },
  };
}

// Plateau the loader holds at while waiting for the operation to finish.
const PLATEAU_PERCENT = 90;
// Fallback when a caller does not specify an expected duration. Covers the
// shorter flows (edit-with-AI, AI Fashion, photoshoots) that sit near this.
const DEFAULT_EXPECTED_DURATION_SECONDS = 22;

// ─── Per-run profile (matches iOS configureProgressProfile) ──────────────────
interface ProgressProfile {
  expectedDuration: number;   // seconds
  plateauPercent: number;     // hold point while waiting (see PLATEAU_PERCENT)
  jitterIntervalLow: number;
  jitterIntervalHigh: number;
  earlyPauseChance: number;
  midPauseChance: number;
  latePauseChance: number;
}

function buildProfile(seed: number, expectedDurationSeconds: number): ProgressProfile {
  const rng = makeLcg(seed);
  const jitterBias = rng.next();
  const pauseBias  = rng.next();

  return {
    expectedDuration:   expectedDurationSeconds,
    plateauPercent:     PLATEAU_PERCENT,
    jitterIntervalLow:  0.45 + jitterBias * 0.65,
    jitterIntervalHigh: 1.05 + jitterBias * 1.55,
    earlyPauseChance:   0.04 + pauseBias * 0.10,
    midPauseChance:     0.09 + pauseBias * 0.15,
    latePauseChance:    0.16 + pauseBias * 0.20,
  };
}

// ─── Loading fraction curve (matches iOS EditWithAIRenderProgressBadge) ───────
function loadingFraction(elapsed: number, profile: ProgressProfile): number {
  const plateau = profile.plateauPercent / 100;
  if (elapsed <= profile.expectedDuration) {
    const t = Math.min(1, elapsed / profile.expectedDuration);
    return plateau * (1 - Math.pow(1 - t, 2.2));
  }
  const overtime = elapsed - profile.expectedDuration;
  const headroom = Math.max(0, 0.99 - plateau);
  const approach = 1 - Math.exp(-overtime / 7.5);
  return plateau + headroom * approach;
}

// ─────────────────────────────────────────────────────────────────────────────

export interface LoadingPercentageProps {
  /** Optional seed for deterministic per-run behavior. Defaults to random on mount. */
  seed?: number;
  /** Expected operation duration (s); the curve reaches the plateau around this point.
   *  Defaults to {@link DEFAULT_EXPECTED_DURATION_SECONDS}. */
  expectedDurationSeconds?: number;
  /** Extra bottom spacing (px) — used when gradient is offset inside a larger container. */
  offsetBottom?: number;
  /** Extra right spacing (px) — used when gradient is offset inside a larger container. */
  offsetRight?: number;
}

const LoadingPercentage: React.FC<LoadingPercentageProps> = ({
  seed,
  expectedDurationSeconds = DEFAULT_EXPECTED_DURATION_SECONDS,
  offsetBottom = 0,
  offsetRight = 0,
}) => {
  const [displayText, setDisplayText] = useState('0%');

  const stateRef = useRef<{
    profile: ProgressProfile;
    rng: ReturnType<typeof makeLcg>;
    startTimeSec: number;
    lastShownFraction: number;
    lastShownPercent: number;
    nextJitterChangeSec: number;
    jitterOffset: number;
    lastFrameSec: number;
    rafId: number | null;
  } | null>(null);

  useEffect(() => {
    const effectiveSeed = seed != null ? seed : (Math.random() * 0xffffffff) >>> 0;
    const profile   = buildProfile(effectiveSeed, expectedDurationSeconds);
    // Offset the runtime RNG from the profile RNG so they don't alias.
    const rng       = makeLcg(effectiveSeed ^ 0xdeadbeef);
    const nowSec    = performance.now() / 1000;

    stateRef.current = {
      profile,
      rng,
      startTimeSec:         nowSec,
      lastShownFraction:    0,
      lastShownPercent:     0,
      nextJitterChangeSec:  nowSec,
      jitterOffset:         0,
      lastFrameSec:         nowSec,
      rafId:                null,
    };

    const frame = (timestamp: number) => {
      const st = stateRef.current;
      if (!st) return;

      const nowS = timestamp / 1000;
      const dt   = nowS - st.lastFrameSec;
      st.lastFrameSec = nowS;

      const elapsed = nowS - st.startTimeSec;
      const frac    = loadingFraction(elapsed, st.profile);

      // ── Update jitter offset on schedule ──
      if (nowS >= st.nextJitterChangeSec) {
        st.nextJitterChangeSec =
          nowS + st.rng.randRange(st.profile.jitterIntervalLow, st.profile.jitterIntervalHigh);
        const p = st.lastShownPercent;
        if      (p < 5)  st.jitterOffset = st.rng.randInt(0, 3);
        else if (p < 70) st.jitterOffset = st.rng.randInt(-2, 4);
        else if (p < 90) st.jitterOffset = st.rng.randInt(-3, 3);
        else             st.jitterOffset = st.rng.randInt(-4, 2);
      }

      const maxBeforeReveal = st.profile.plateauPercent / 100;
      const base = Math.min(frac, maxBeforeReveal);

      const target =
        st.lastShownPercent < 5
          ? Math.min(maxBeforeReveal, Math.max(st.lastShownFraction, base))
          : Math.min(maxBeforeReveal, Math.max(st.lastShownFraction, base + st.jitterOffset / 100));

      const remaining = target - st.lastShownFraction;

      if (remaining > 0) {
        // ── Pause near target ──
        const pauseChance =
          st.lastShownPercent < 80 ? st.profile.earlyPauseChance :
          st.lastShownPercent < 90 ? st.profile.midPauseChance :
                                     st.profile.latePauseChance;

        if (!(remaining <= 0.02 && st.rng.next() < pauseChance)) {
          // ── Step limit by stage (fraction/sec) ──
          const p = st.lastShownPercent;
          const maxRate =
            p < 45 ? (st.rng.next() < 0.28 ? 0.17  : 0.135) :
            p < 80 ? (st.rng.next() < 0.22 ? 0.095 : 0.072) :
            p < 90 ? 0.038 :
                     (st.rng.next() < 0.12 ? 0.014 : 0.009);

          const step = Math.min(remaining, maxRate * dt);
          st.lastShownFraction = Math.min(0.99, st.lastShownFraction + step);
          const newPercent     = Math.min(99, Math.floor(st.lastShownFraction * 100));

          if (newPercent !== st.lastShownPercent) {
            st.lastShownPercent = newPercent;
            setDisplayText(String(newPercent) + '%');
          }
        }
      }

      st.rafId = requestAnimationFrame(frame);
    };

    stateRef.current.rafId = requestAnimationFrame(frame);

    return () => {
      if (stateRef.current?.rafId != null) {
        cancelAnimationFrame(stateRef.current.rafId);
      }
      stateRef.current = null;
    };
  }, [seed, expectedDurationSeconds]);

  return (
    <AiGlowCircle
      style={{
        position: 'absolute',
        bottom: offsetBottom + 12,
        right: offsetRight + 12,
        zIndex: Z_INDEX.GRADIENT_LOADER + 1,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          color: '#111111',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '-0.066px',
          lineHeight: '24px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayText}
      </span>
    </AiGlowCircle>
  );
};

export default LoadingPercentage;

